<template>
  <div class="kira-chat-page">
    <div class="kira-chat-shell">
      <div class="kira-chat-sidebar">
        <h3 class="mb-1">
          Kira
        </h3>
        <p class="text-muted mb-2">
          Belső AI asszisztens a Monolit adataihoz és folyamataihoz.
        </p>

        <div class="kira-suggestion-list">
          <button
            v-for="(suggestion, index) in suggestions"
            :key="index"
            type="button"
            class="kira-suggestion"
            @click="applySuggestion(suggestion)"
          >
            {{ suggestion }}
          </button>
        </div>

        <b-button
          variant="outline-secondary"
          class="mt-2"
          @click="resetConversation"
        >
          Beszélgetés törlése
        </b-button>
      </div>

      <div class="kira-chat-main">
        <div
          ref="messagesContainer"
          class="kira-chat-messages"
        >
          <div
            v-for="(message, index) in messages"
            :key="index"
            class="kira-message-row"
            :class="message.role === 'user' ? 'is-user' : 'is-assistant'"
          >
            <div class="kira-message-meta">
              {{ message.role === 'user' ? 'Te' : 'Kira' }}
            </div>
            <div class="kira-message-bubble">
              {{ message.content }}
            </div>
          </div>

          <div
            v-if="isLoading"
            class="kira-message-row is-assistant"
          >
            <div class="kira-message-meta">
              Kira
            </div>
            <div class="kira-message-bubble kira-message-bubble-loading">
              <b-spinner
                small
                class="mr-50"
              />
              Gondolkodom...
            </div>
          </div>
        </div>

        <div class="kira-chat-composer">
          <b-form-textarea
            v-model="draft"
            rows="4"
            max-rows="8"
            placeholder="Írd be a kérdésedet vagy utasításodat..."
            @keydown.enter.exact.prevent="sendMessage"
            @keydown.enter.shift.exact.stop
          />

          <div class="kira-chat-actions">
            <small class="text-muted">
              `Enter` küldés, `Shift + Enter` új sor
            </small>
            <b-button
              variant="primary"
              :disabled="isLoading || !draft.trim()"
              @click="sendMessage"
            >
              Küldés
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { BButton, BFormTextarea, BSpinner } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const initialAssistantMessage = {
  role: 'assistant',
  content: 'Szia, Kira vagyok. Kérdezhetsz a Monolit adatairól, projektekről vagy feladatokról.',
}

export default {
  components: {
    BButton,
    BFormTextarea,
    BSpinner,
  },
  data() {
    return {
      draft: '',
      isLoading: false,
      messages: [initialAssistantMessage],
      suggestions: [
        'A Kossuth utcai projekt hány napos késésben van?',
        'Milyen lejárt feladataim vannak?',
        'Hozz létre egy fontos feladatot Péternek holnapra.',
      ],
    }
  },
  methods: {
    applySuggestion(suggestion) {
      this.draft = suggestion
    },
    resetConversation() {
      this.messages = [initialAssistantMessage]
      this.draft = ''
    },
    async sendMessage() {
      const content = this.draft.trim()
      if (!content || this.isLoading) return

      const userMessage = {
        role: 'user',
        content,
      }

      this.messages.push(userMessage)
      this.draft = ''
      this.isLoading = true
      this.scrollToBottom()

      try {
        const response = await axios({
          method: 'post',
          url: 'kira/chat',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          data: {
            messages: this.messages,
          },
        })

        this.messages.push({
          role: 'assistant',
          content: response.data.message,
        })

        this.scrollToBottom()
      } catch (error) {
        const message = error?.response?.data?.message || 'A Kira kapcsolat hibára futott.'

        this.$toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'XOctagonIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoading = false
        this.scrollToBottom()
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.kira-chat-page {
  padding: 1.5rem;
  min-height: calc(100vh - 140px);
  background: linear-gradient(135deg, #f4f7fb 0%, #eef2f7 100%);
}

.kira-chat-shell {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 1.5rem;
  min-height: calc(100vh - 188px);
}

.kira-chat-sidebar,
.kira-chat-main {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 12px 40px rgba(34, 41, 47, 0.08);
}

.kira-chat-sidebar {
  padding: 1.5rem;
}

.kira-suggestion-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.kira-suggestion {
  border: 1px solid #dfe6f0;
  background: #f8fafc;
  border-radius: 12px;
  padding: 0.9rem 1rem;
  text-align: left;
  color: #334155;
  transition: 0.2s ease;
}

.kira-suggestion:hover {
  background: #eef4ff;
  border-color: #7367f0;
}

.kira-chat-main {
  display: grid;
  grid-template-rows: 1fr auto;
  overflow: hidden;
}

.kira-chat-messages {
  padding: 1.5rem;
  overflow-y: auto;
}

.kira-message-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.kira-message-row.is-user {
  align-items: flex-end;
}

.kira-message-row.is-assistant {
  align-items: flex-start;
}

.kira-message-meta {
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 0.35rem;
}

.kira-message-bubble {
  max-width: min(80%, 820px);
  padding: 0.95rem 1rem;
  border-radius: 16px;
  white-space: pre-wrap;
  line-height: 1.5;
}

.is-user .kira-message-bubble {
  background: #7367f0;
  color: #fff;
  border-bottom-right-radius: 6px;
}

.is-assistant .kira-message-bubble {
  background: #f5f7fb;
  color: #334155;
  border-bottom-left-radius: 6px;
}

.kira-message-bubble-loading {
  display: flex;
  align-items: center;
}

.kira-chat-composer {
  border-top: 1px solid #edf2f7;
  padding: 1rem 1.5rem 1.25rem;
}

.kira-chat-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
}

@media (max-width: 991px) {
  .kira-chat-page {
    padding: 1rem;
  }

  .kira-chat-shell {
    grid-template-columns: 1fr;
    min-height: auto;
  }

  .kira-chat-sidebar {
    order: 2;
  }

  .kira-chat-main {
    min-height: 70vh;
  }

  .kira-message-bubble {
    max-width: 100%;
  }
}
</style>
